import React, { useEffect, useState } from "react";
import { logoSources } from "../lib/logoSources";
import { useMintLogo } from "../lib/mintLogos";
import "./tokenLogo.css";

/**
 * A token's logo image, falling back to symbol/name initials — for real,
 * not just when `image` is empty. An `<img>` with a dead or non-image URL
 * (common: a stale upload, a metadata JSON mistaken for an image) renders
 * the browser's native broken-image glyph with no fallback unless something
 * catches the error — every "logo or initials" spot in the app had this bug.
 */
const CYCLE = ["var(--accent)", "var(--accent-2)", "var(--accent-3)", "var(--ok)"];

// One logo URL as the list of places it can load from (logoSources): the
// host first, so an SVG logo the proxy refuses still shows. A URL that's
// already our proxy's goes back to its host first too.
const PROXY = /^\/api\/token-meta\/image\?uri=([^&]+)/;
const sourcesOf = (image) => {
  if (Array.isArray(image)) return image.filter(Boolean);
  if (!image) return [];
  const proxied = image.match(PROXY);
  return logoSources(proxied ? decodeURIComponent(proxied[1]) : image);
};

/**
 * `mint`, when given, is the fallback: if the caller has no logo, or every
 * place it can load from fails, the mint's own logo as DAS indexed it is
 * tried (lib/mintLogos.js). Pass it wherever a token is shown — the rule
 * is that every token mention carries its official logo.
 *
 * `variant="cycle"` + `index` is opt-in, used only by the My Tokens list
 * card per the design spec (a solid accent ground cycling by list position).
 * Every other consumer (detail page header, the token picker) keeps the
 * flat, uncolored default — deliberately, per an earlier explicit "no
 * colored bg" fix for this component.
 */
export const TokenLogo = ({ image, mint, label, className = "", variant, index = 0 }) => {
  const given = sourcesOf(image);
  const [attempt, setAttempt] = useState(0);
  // Only once the caller's own sources are gone does the mint get looked up.
  const das = useMintLogo(mint && attempt >= given.length ? mint : null);
  const sources = [...new Set([...given, ...das.flatMap(logoSources)])];
  const key = `${given.join(" ")}|${mint || ""}`;
  const failed = attempt >= sources.length;
  // Initials show until the image has actually arrived, not a blank tile
  // while a slow host (an IPFS gateway, our image proxy's first fetch) works.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setAttempt(0);
    setLoaded(false);
  }, [key]);

  const initials = (label || "?").trim().slice(0, 2).toUpperCase();
  const style =
    variant === "cycle"
      ? {
          background: CYCLE[index % CYCLE.length],
          color: "#000",
          border: "var(--border-w) solid var(--ink)",
        }
      : {
          background: "var(--surface)",
          color: "var(--ink)",
          border: "var(--border-w) solid var(--ink)",
        };

  if (!failed) {
    return (
      <span className={className} style={style}>
        {!loaded && initials}
        <img
          key={sources[attempt]}
          src={sources[attempt]}
          alt=""
          onLoad={() => setLoaded(true)}
          onError={() => setAttempt((n) => n + 1)}
          style={loaded ? undefined : { display: "none" }}
        />
      </span>
    );
  }
  return (
    <span className={className} style={style}>
      {initials}
    </span>
  );
};

// The symbol in a text like "1,000 BONK", for the initials a token with no
// logo shows: its last word that starts with a letter.
const symbolIn = (text) => (typeof text === "string" ? (text.match(/[A-Za-z$][\w$.-]*/g) || []).pop() || "" : "");

/**
 * A token named in running text, a row or a card, with its logo in front:
 * `<TokenTag mint={mint} image={logo}>{symbol}</TokenTag>`. `size="lg"`
 * for headers; `label` names it for the initials when the text doesn't.
 * Styles: tokenLogo.css, so it works on any page.
 */
export const TokenTag = ({ mint, image, size, label, children }) => (
  <span className="hp-tok">
    <TokenLogo className={`hp-tok-logo${size === "lg" ? " lg" : ""}`} image={image} mint={mint} label={label || symbolIn(children)} />
    {children}
  </span>
);
